<template>
  <div class="product-prices-widget">
    <div class="product-prices-grid">
      <div v-for="(item, index) in items" :key="index" class="product-card">
        <div class="product-image" v-if="item.image">
          <img :src="item.image" :alt="item.name" />
        </div>
        <div class="product-info">
          <span class="product-name">{{ item.name }}</span>
          <span class="product-details">
            <template v-if="item.purityDisplay">{{ texts.labelPurity }}: {{ item.purityDisplay }} &middot; </template>
            <template v-if="item.unit !== 'PCS'">{{ texts.labelWeight }}: </template>
            {{ formatWeight(item.weight, item.unit) }}
          </span>
          <span class="product-price">{{ texts.labelPrice }}: {{ formatPrice(calculatePrice(item)) }}</span>
          <span class="product-updated" v-if="lastUpdated">{{ texts.labelUpdated }} {{ formattedLastUpdated }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductPrices',
  props: {
    initialData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      items: [],
      prices: {},
      storeCode: null,
      currency: 'NOK',
      lastUpdated: null,
      apiUrl: null,
      refreshInterval: null,
      texts: {
        labelPurity: 'Karat',
        labelWeight: 'Vekt',
        labelPrice: 'Pris',
        labelUpdated: 'Pris oppdatert',
        labelPcs: 'stk',
      }
    };
  },
  created() {
    if (this.initialData) {
      if (this.initialData.items) {
        this.items = this.initialData.items;
      }
      if (this.initialData.prices) {
        this.prices = this.initialData.prices;
      }
      if (this.initialData.storeCode) {
        this.storeCode = String(this.initialData.storeCode).toLowerCase();
      }
      if (this.initialData.currency) {
        this.currency = this.initialData.currency;
      }
      if (this.initialData.lastUpdated) {
        this.lastUpdated = this.initialData.lastUpdated;
      }
      if (this.initialData.apiUrl) {
        this.apiUrl = this.initialData.apiUrl;
      }
      if (this.initialData.texts) {
        this.texts = { ...this.texts, ...this.initialData.texts };
      }
    }

    if (this.apiUrl) {
      this.refreshInterval = setInterval(() => {
        this.fetchPrices();
      }, 30 * 60 * 1000);
    }
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  computed: {
    formattedLastUpdated() {
      if (!this.lastUpdated) return '';
      const date = new Date(this.lastUpdated);
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      return day + '.' + month + '.' + year;
    }
  },
  methods: {
    calculatePrice(item) {
      // Prices are keyed by country_sku, items carry puritySku.
      var key = item && item.puritySku ? String(item.puritySku) : '';
      if (!key) return 0;
      var pricePerGram = parseFloat(this.prices[key]) || 0;
      return item.weight * pricePerGram;
    },
    formatPrice(value) {
      return value.toLocaleString('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + this.currency;
    },
    formatWeight(weight, unit) {
      if (unit === 'PCS') {
        return Math.round(weight) + ' ' + this.texts.labelPcs;
      }
      var decimals = weight % 1 === 0 ? 0 : 1;
      return weight.toLocaleString('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + ' g';
    },
    async fetchPrices() {
      if (!this.apiUrl) return;
      try {
        var response = await fetch(this.apiUrl);
        var data = await response.json();
        if (data.prices) {
          this.prices = data.prices;
        }
        if (data.currency) {
          this.currency = data.currency;
        }
        if (data.lastUpdated) {
          this.lastUpdated = data.lastUpdated;
        }
      } catch (error) {
        console.error('Failed to fetch prices:', error);
      }
    }
  }
};
</script>

<style scoped>
.product-prices-widget {
  width: 100%;
  background: #fff;
  padding: 24px;
  border-radius: 8px;
}

.product-prices-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.product-card {
  display: flex;
  flex-direction: column;
  background: #fff;
}

.product-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border-radius: 4px;
  margin-bottom: 12px;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-name {
  font-weight: 700;
  font-size: 1.05em;
  color: #1a1a1a;
}

.product-details {
  font-size: 0.85em;
  color: #555;
}

.product-price {
  font-weight: 700;
  font-size: 1.1em;
  color: #b8860b;
  margin-top: 4px;
}

.product-updated {
  font-size: 0.75em;
  color: #888;
  margin-top: 2px;
}

@media (max-width: 768px) {
  .product-prices-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
}

@media (max-width: 480px) {
  .product-prices-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}
</style>
